import { useMutation } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { z } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { LoaderIcon } from '@/components/ui/loader-icon';
import { useToast } from '@/components/ui/hooks/use-toast';
import { importCoachUsersFromV1 } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';

const formSchema = z.object({});
type Form = z.infer<typeof formSchema>;
const EMPTY_ERRORS: string[] = [];

export interface ImportCoachesFromV1Props {
  trigger: ReactNode;
}

export function ImportCoachesFromV1({ trigger }: ImportCoachesFromV1Props) {
  const { toast } = useToast();
  const [errorMessages, setErrorMessages] = useState<string[]>(EMPTY_ERRORS);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: importCoachUsersFromV1,
    mutationKey: ['USERS'],
  });

  const onSubmit = ({ closeModal }: SubmitProps<Form>) => {
    setErrorMessages(EMPTY_ERRORS);
    mutateAsync()
      .then((summary) => {
        if (summary.failures.length) {
          setErrorMessages(
            summary.failures.map(
              ({ coachFullName, error }) =>
                `Failed to import teacher ${coachFullName}: ${JSON.stringify(error, undefined, 2)}`,
            ),
          );
        }
        if (summary.isCompletelySuccessful()) {
          toast({
            title: 'Import complete',
            description: `Successfully imported ${summary.actualTotal} teacher(s).`,
          });
          closeModal();
          return;
        }
        if (summary.isCompleteFailure()) {
          toast({
            title: 'Import failed',
            description: 'See the modal for more detailed error messages.',
          });
          return;
        }
        toast({
          variant: 'destructive',
          title: 'Import partially failed',
          description: `Although importing succeeded for ${summary.succeeded} teacher(s), some teachers could not be imported. See the modal for more detailed messages.`,
        });
      })
      .catch((error) => {
        const airServerError = airServerErrorFrom(error);
        const errorMessage = airServerError?.userFacingMessage || 'Unknown';

        toast({
          title: 'Import teachers: internal error',
          description: errorMessage,
        });
      });
  };

  return (
    <FormModal
      modalTitle="Import teachers from V1 Airtable"
      formSchema={formSchema}
      onSubmit={onSubmit}
      confirmBtnLabel="Import"
      trigger={trigger}
      onClose={() => setErrorMessages(EMPTY_ERRORS)}
    >
      {() => {
        return (
          <>
            <div className="grid grid-cols-1 gap-2 py-4">
              <ol>
                <li>1. Confirm in Airtable that the teachers to import have their "Sync with V2" fields checked.</li>
                <li>
                  2. Remember that you can attempt to sync the same teacher again. If an existing teacher matches an
                  Airtable teacher record, their account is instead overwritten with the latest data from Airtable. No
                  new account would be created for this teacher.
                </li>
              </ol>
            </div>
            {isPending && <LoaderIcon />}
            {errorMessages.length > 0 && (
              <div className="w-full mb-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error while importing teachers from V1 Airtable</AlertTitle>
                  <AlertDescription>
                    {errorMessages.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </>
        );
      }}
    </FormModal>
  );
}
